import React from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import type { FieldPath } from 'react-hook-form/dist/types/path';
import type { FormFieldVariant, FormInputFieldInputType } from '../enums';
import { FormInputFieldViewVariant } from '../enums';
import FormField from '../FormField/FormField';
import type { FormFieldProps, FormFieldType } from '../types';
import { FieldType } from '../types';
import localStyles from './InputField.module.css';
import InputFieldInternal from './InputFieldInternal';

export interface MirrorWatcher<
  MirrorT extends keyof DataT,
  DataT extends FieldValues,
  NameT extends FieldPath<DataT>
> {
  /**
   * The field that updates its value based on the value of this input
   */
  targetField: MirrorT;

  /**
   * Callback function that returns a transformed value of the current field
   *
   * @param value the current value for the input field
   */
  transformer: (value: string) => DataT[NameT];
}

/**
 * Input Field definition to render the input field. NameT is the name of the field and DataT is the definition of bean
 * that holds all the fields for the form.
 */
export interface InputFieldSpec<
  NameT extends FieldPath<DataT>,
  DataT extends FieldValues,
  FormFieldInputTypeT extends FormInputFieldInputType = FormInputFieldInputType.TEXT
> extends FormFieldType<NameT, DataT, FormFieldVariant.INPUT> {
  /**
   * Input type for the field.
   */
  inputType: FormInputFieldInputType;
  /**
   * View variant of the input field. Should specify how the field should render when used across any form.
   */
  viewVariant?: FormInputFieldViewVariant;
  /**
   * Callback when a key is pressed within the input.
   * @callback
   */
  onKeyDown?: (event: React.KeyboardEvent, formMethods: UseFormReturn<DataT>) => void | undefined;
  /**
   * Callback when a previously pressed key is released within the input.
   * @callback
   */
  onKeyUp?: (event: React.KeyboardEvent, formMethods: UseFormReturn<DataT>) => void | undefined;
  /**
   * Callback when the input field is blurred.
   * @callback
   */
  onBlur?: (event: React.KeyboardEvent, formMethods: UseFormReturn<DataT>) => void | undefined;
  /**
   * Callback to update a target field value based on the value of this field. Only applies to text field.
   */
  onMirror?: FormFieldInputTypeT extends FormInputFieldInputType.TEXT
    ? MirrorWatcher<FieldPath<DataT>, DataT, NameT>
    : never;
  /**
   * Callback prior to the value of a field being set into the backing form data. This
   * allows for specific use cases where forms need to transform a value before being reflected
   * in the backing input. The return value will be the value use.
   */
  onBeforeChange?: (event: React.ChangeEvent<HTMLInputElement>) => DataT[NameT];
  /**
   * aria label for the field
   */
  ariaLabel?: string;

  /**
   * Define a custom placeholder text key for the input field
   */
  placeholderOverride?: string;
}

/**
 * Renders input field for the form
 *
 * @author Manish Shrestha
 */
const InputField = <NameT extends FieldPath<DataT>, DataT extends FieldValues>({
  fieldSpec,
  formId,
  i18n,
  className,
  formMethods,
  submitOnChange,
  onFormSubmit,
  allFields
}: FormFieldProps<
  NameT,
  DataT,
  InputFieldSpec<NameT, DataT, FormInputFieldInputType>
>): React.ReactElement => {
  const cx = useClassNameMapper(localStyles);
  const { hasMessage, formatMessage, loading: textLoading } = i18n;
  const { viewVariant, name } = fieldSpec;
  const hasFieldTitle = hasMessage(`${formId}.${name}.fieldTitle`);

  if (textLoading) {
    return null;
  }

  return (
    <>
      {hasFieldTitle && (
        <div className={cx('lia-g-mb-15')}>
          <h4>{formatMessage(`${formId}.${name}.fieldTitle`)}</h4>
        </div>
      )}
      <FormField<NameT, DataT, FieldType.CONTROLLED>
        fieldSpec={fieldSpec}
        fieldType={FieldType.CONTROLLED}
        formId={formId}
        i18n={i18n}
        className={cx(
          {
            'lia-g-form-field-no-border': viewVariant === FormInputFieldViewVariant.NO_BORDER
          },
          className
        )}
        formMethods={formMethods}
        submitOnChange={submitOnChange}
        onFormSubmit={onFormSubmit}
        allFormFields={allFields}
      >
        {({ render }) => {
          return (
            <InputFieldInternal
              render={render}
              fieldSpec={fieldSpec}
              formProps={{ formId, i18n, formMethods }}
            />
          );
        }}
      </FormField>
    </>
  );
};
export default InputField;
